import React, { FunctionComponent, useRef, useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { useHotkeys } from 'react-hotkeys-hook';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { debounce, range } from 'lodash';
import LoadingOverlay from '../LoadingOverlay';
import { FormAnnotationLayer } from './FormAnnotationLayer';
import { Change, AnnotationLike, AnnotationAddMetaType } from './types';
import { useChangeTracker } from './hooks/useChangeTracker';
import './FormPDFEditor.scss';
import { FormPDFEditorSideBar } from './FormPDFEditorSideBar';
import useLocalStorage from '../../util/useLocalStorage';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface Props {
  formId: string;
  formUrl: string;
  fetching: boolean;
  onSave: (changes: Change[]) => void;
}

export const FormPDFEditor: FunctionComponent<Props> = ({
  formId,
  formUrl,
  fetching,
  onSave,
}) => {
  // add an import for opencv to enable smart add
  useEffect(() => {
    if (document.querySelector('#opencvImport')) return;

    // Create a script tag for opencv
    const script = document.createElement('script');
    script.src = '/opencv.js';
    script.id = 'opencvImport';
    script.async = true;

    // Get the first script tag on the page and insert the new node before it
    const ref = document.querySelector('script');
    ref?.parentNode?.insertBefore(script, ref);
  }, []);

  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const setScaleDebounced = useRef(debounce(setScale, 100));
  const [useSmartAddAnnotation, setUseSmartAddAnnotation] = useLocalStorage(
    'useSmartAddAnnotation',
    false,
  );

  // track all changes done to the pdf so that rosetta can apply them later
  const {
    changes,
    recordChange,
    undoChange,
    clearChanges,
  } = useChangeTracker();
  useHotkeys('cmd+z,ctrl+z', undoChange);

  // if a new form is loaded clear all recorded changes
  useEffect(() => clearChanges(), [formUrl, clearChanges]);

  // the current annotation type being added to the pdf
  const [addAnnotation, setAddAnnotation] = useState<AnnotationAddMetaType>();
  useHotkeys('esc', () => setAddAnnotation(undefined));

  // the currently selected annotation
  const [selectedAnnotation, setSelectedAnnotation] = useState<
    AnnotationLike
  >();

  // to reset the view, set TransformWrapper to controlled for a render
  // with a reset scale/position, and immediately set it back to uncontrolled next render
  const [resetView, setResetView] = useState(false);
  useEffect(() => {
    if (resetView) setResetView(false);
  }, [resetView]);

  return (
    <LoadingOverlay active={fetching}>
      <div className="form-pdf-editor-container">
        <TransformWrapper
          scale={resetView ? 1 : undefined}
          positionX={resetView ? 0 : undefined}
          positionY={resetView ? 0 : undefined}
          defaultScale={1}
          defaultPositionX={0}
          defaultPositionY={0}
          options={{
            centerContent: false,
          }}
          onZoomChange={(newScale: { scale: number }) =>
            setScaleDebounced.current(newScale.scale)
          }
        >
          <TransformComponent>
            <Document
              className="pdf-doc"
              file={formUrl}
              onLoadSuccess={async pdf => {
                setNumPages(pdf.numPages);
              }}
            >
              {range(1, numPages + 1).map(pageNumber => (
                <Page
                  key={pageNumber}
                  pageNumber={pageNumber}
                  renderTextLayer={false}
                >
                  <FormAnnotationLayer
                    pageNumber={pageNumber}
                    scale={scale}
                    addAnnotation={addAnnotation}
                    smartAddAnnotation={useSmartAddAnnotation}
                    setAddAnnotation={setAddAnnotation}
                    selectedAnnotation={selectedAnnotation}
                    onAnnotationSelected={setSelectedAnnotation}
                    changes={changes}
                    recordChange={recordChange}
                  />
                </Page>
              ))}
            </Document>
          </TransformComponent>
        </TransformWrapper>
        <FormPDFEditorSideBar
          formId={formId}
          resetView={() => setResetView(true)}
          onSave={() => onSave(changes)}
          changes={changes}
          recordChange={recordChange}
          undoChange={undoChange}
          selectedAnnotation={selectedAnnotation}
          clearSelectedAnnotation={() => setSelectedAnnotation(undefined)}
          addingAnnotation={addAnnotation}
          onAddAnnotation={setAddAnnotation}
          useSmartAddAnnotation={useSmartAddAnnotation}
          setUseSmartAddAnnotation={setUseSmartAddAnnotation}
        />
      </div>
      <canvas
        id="draftCanvas"
        width="300"
        height="300"
        style={{ height: 300, display: 'none' }}
      />
    </LoadingOverlay>
  );
};
